import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JourneyLadder from '../../components/JourneyLadder';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { loadTopicLessonsCatalog } from '../../services/topicLessons';
import { TopicProgressService } from '../../services/TopicProgressService';

export default function TopicLessonsPathPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { level: rawLevel } = useParams();
  const { languagePrefs } = useAuth();
  const { languages } = useLanguages();
  const level = String(rawLevel || 'a1').trim().toLowerCase();
  const targetCode = languagePrefs?.targetLanguage || '';
  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${targetCode}`
    : 'guest';
  const targetLabel = languages.find((language) => language.code === targetCode)?.label
    || targetCode.toUpperCase()
    || t('learningPaths.aLanguage');
  const [chapters, setChapters] = useState([]);
  const [status, setStatus] = useState('loading');
  const [progress, setProgress] = useState({ completedIds: [], completedCount: 0, percent: 0, isComplete: false });
  const lessonIds = useMemo(() => chapters.map((chapter) => chapter.id), [chapters]);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setChapters([]);
    loadTopicLessonsCatalog(targetCode, level)
      .then((loaded) => {
        if (!active) return;
        setChapters(loaded);
        setStatus(loaded.length ? 'ready' : 'unavailable');
      })
      .catch(() => {
        if (!active) return;
        setChapters([]);
        setStatus('unavailable');
      });
    return () => { active = false; };
  }, [level, targetCode]);

  useEffect(() => {
    const refresh = () => setProgress(TopicProgressService.snapshot(langPair, targetCode, level, lessonIds));
    refresh();
    window.addEventListener('topic-progress-changed', refresh);
    return () => window.removeEventListener('topic-progress-changed', refresh);
  }, [langPair, lessonIds, level, targetCode]);

  const completed = new Set(progress.completedIds);
  const items = chapters.map((chapter) => ({ ...chapter, completed: completed.has(chapter.id) }));
  const nextChapter = items.find((chapter) => !chapter.completed);

  const markFinished = (chapter) => {
    setProgress(TopicProgressService.completeLesson(
      langPair,
      targetCode,
      level,
      chapter.id,
      lessonIds,
    ));
  };

  const openLesson = (chapter) => navigate(`/topic-lessons/${level}/${chapter.id}`);
  const goToNext = () => { if (nextChapter) openLesson(nextChapter); };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-fuchsia-50 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
      <main className="mx-auto max-w-5xl">
        <Link to={`/?level=${level}`} className="inline-flex items-center gap-2 text-sm font-black text-violet-700 hover:text-violet-900">
          <span aria-hidden="true">←</span> {t('journeyPages.backHome')}
        </Link>

        <section className="mt-6 overflow-hidden rounded-3xl bg-gradient-to-br from-violet-700 via-purple-700 to-fuchsia-700 p-7 text-white shadow-xl sm:p-10">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-white/70">{t('learningPaths.topicTitle')} · {level.toUpperCase()}</p>
          <h1 className="mt-3 text-3xl font-black sm:text-5xl">{t('journeyPages.topicHeading', { language: targetLabel })}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">{t('learningPaths.topicDesc')}</p>
          {status === 'ready' && (
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex justify-between text-xs font-black"><span>{t('journeyPages.overallProgress')}</span><span>{progress.completedCount}/{chapters.length} · {progress.percent}%</span></div>
                <div className="mt-2 h-3 overflow-hidden rounded-full bg-white/20"><div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: `${progress.percent}%` }} /></div>
              </div>
              {nextChapter && <button type="button" onClick={goToNext} className="rounded-xl bg-white px-5 py-3 text-sm font-black text-violet-700 shadow-md transition hover:-translate-y-0.5">{t('journeyPages.continueJourney')}</button>}
            </div>
          )}
        </section>

        {status === 'loading' && (
          <div className="mt-8 space-y-5" aria-label={t('journeyPages.loadingJourney')}>
            {[1, 2, 3].map((item) => <div key={item} className="h-36 animate-pulse rounded-3xl bg-white" />)}
          </div>
        )}

        {status === 'unavailable' && (
          <section className="mt-8 rounded-3xl border border-violet-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl" aria-hidden="true">📚</span>
            <h2 className="mt-4 text-xl font-black text-slate-900">{t('learningPaths.topicsComingSoon')}</h2>
            <p className="mt-2 text-sm text-slate-500">{t('learningPaths.topicsComingSoonDesc')}</p>
          </section>
        )}

        {status === 'ready' && (
          <section className="mt-10">
            <JourneyLadder
              items={items}
              ariaLabel={t('journeyPages.topicLadder')}
              renderItem={(chapter) => (
                <article
                  id={`topic-${chapter.id}`}
                  role="link"
                  tabIndex={0}
                  onClick={() => openLesson(chapter)}
                  onKeyDown={(event) => { if (event.key === 'Enter') openLesson(chapter); }}
                  className={`cursor-pointer scroll-mt-28 rounded-3xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${chapter.completed ? 'border-emerald-200' : 'border-violet-100'}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-violet-50 text-lg font-black text-violet-700">{chapter.chapterNumber}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <h2 className="text-sm font-black leading-relaxed text-slate-900">{chapter.label}</h2>
                        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${chapter.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-50 text-violet-600'}`}>
                          {chapter.completed ? t('journeyPages.finished') : t('learningPaths.ready')}
                        </span>
                      </div>
                      <p className="mt-2 text-[10px] font-black uppercase tracking-wider text-violet-400">{chapter.type === 'checkpoint' ? t('learningPaths.checkpoint') : chapter.type === 'review' ? t('learningPaths.review') : t('learningPaths.lesson')}</p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button type="button" onClick={(event) => { event.stopPropagation(); openLesson(chapter); }} className="rounded-xl bg-violet-600 px-3 py-2.5 text-xs font-black text-white transition hover:bg-violet-700">{t('journeyPages.openLesson')}</button>
                    <button type="button" onClick={(event) => { event.stopPropagation(); markFinished(chapter); }} disabled={chapter.completed} className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs font-black text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-default disabled:opacity-60">
                      {chapter.completed ? `✓ ${t('journeyPages.finished')}` : t('journeyPages.markFinished')}
                    </button>
                  </div>
                </article>
              )}
            />
          </section>
        )}
      </main>
    </div>
  );
}
